import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTasks, faClipboardCheck, faChartBar, faCalendarAlt, faFileAlt, faClock, faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const InterviewerDashboard = () => {
    const [stats, setStats] = useState({
        totalSurveys: 0,
        completedSurveys: 0,
        pendingSurveys: 0,
        todaySurveys: 0,
        weeklyProgress: 0,
        recentSurveys: []
    });
    const [interviewer, setInterviewer] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            
            // Fetch interviewer profile data
            try {
                const profileResponse = await api.get('/interviewer/profile');
                if (profileResponse.data.success) {
                    setInterviewer(profileResponse.data.data);
                }
            } catch (profileError) {
                console.error('Error fetching interviewer profile:', profileError);
            }
            
            // Mock data for now - in real app, this would come from API
            const mockData = {
                totalSurveys: 24,
                completedSurveys: 18,
                pendingSurveys: 6,
                todaySurveys: 3,
                weeklyProgress: 75,
                recentSurveys: [
                    { id: 1, title: 'Enquête Ménage - Zone A', status: 'completed', date: '2024-01-15', duration: '45 min' },
                    { id: 2, title: 'Enquête Ménage - Zone B', status: 'in_progress', date: '2024-01-15', duration: '30 min' },
                    { id: 3, title: 'Enquête Ménage - Zone C', status: 'pending', date: '2024-01-16', duration: '0 min' },
                    { id: 4, title: 'Enquête Ménage - Zone D', status: 'completed', date: '2024-01-14', duration: '50 min' },
                    { id: 5, title: 'Enquête Ménage - Zone E', status: 'pending', date: '2024-01-17', duration: '0 min' }
                ]
            };
            
            setStats(mockData);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return faCheckCircle;
            case 'in_progress': return faClock;
            case 'pending': return faExclamationCircle;
            default: return faExclamationCircle;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'text-green-600';
            case 'in_progress': return 'text-red-600';
            case 'pending': return 'text-yellow-600';
            default: return 'text-gray-600';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'completed': return 'Terminée';
            case 'in_progress': return 'En cours';
            case 'pending': return 'En attente';
            default: return 'Inconnu';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            <div className="relative z-10 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">Tableau de Bord Enquêteur</h1>
                        <p className="text-gray-600">Gérez vos enquêtes et suivez votre progression</p>
                    </div>

                    {/* Bento Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Progress Overview - Large Card */}
                        <div className="lg:col-span-8 bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900">Progression des Enquêtes</h2>
                                <FontAwesomeIcon icon={faChartBar} className="text-3xl text-red-600" />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                <div className="text-center p-4 bg-gray-50 rounded-xl">
                                    <div className="text-3xl font-bold text-gray-900">{stats.totalSurveys}</div>
                                    <div className="text-sm text-gray-600">Total Enquêtes</div>
                                </div>
                                <div className="text-center p-4 bg-green-50 rounded-xl">
                                    <div className="text-3xl font-bold text-green-600">{stats.completedSurveys}</div>
                                    <div className="text-sm text-gray-600">Terminées</div>
                                </div>
                                <div className="text-center p-4 bg-yellow-50 rounded-xl">
                                    <div className="text-3xl font-bold text-yellow-600">{stats.pendingSurveys}</div>
                                    <div className="text-sm text-gray-600">En Attente</div>
                                </div>
                                <div className="text-center p-4 bg-red-50 rounded-xl">
                                    <div className="text-3xl font-bold text-red-600">{stats.todaySurveys}</div>
                                    <div className="text-sm text-gray-600">Aujourd'hui</div>
                                </div>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="mt-6">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium text-gray-700">Progression Hebdomadaire</span>
                                    <span className="text-sm font-bold text-gray-900">{stats.weeklyProgress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div 
                                        className="bg-gradient-to-r from-red-500 to-red-600 h-3 rounded-full transition-all duration-300"
                                        style={{ width: `${stats.weeklyProgress}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="lg:col-span-4 bg-gradient-to-br from-red-50 to-red-100 rounded-3xl p-6 shadow-xl border border-red-200">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Actions Rapides</h3>
                            <div className="space-y-3">
                                <button className="w-full flex items-center p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors">
                                    <FontAwesomeIcon icon={faTasks} className="text-red-600 mr-3" />
                                    <span className="font-medium">Nouvelle Enquête</span>
                                </button>
                                <button className="w-full flex items-center p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors">
                                    <FontAwesomeIcon icon={faFileAlt} className="text-red-600 mr-3" />
                                    <span className="font-medium">Mes Rapports</span>
                                </button>
                                <button className="w-full flex items-center p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-red-600 mr-3" />
                                    <span className="font-medium">Planning</span>
                                </button>
                            </div>
                        </div>

                        {/* Today's Tasks */}
                        <div className="lg:col-span-8 bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-gray-900">Enquêtes d'Aujourd'hui</h3>
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-xl text-gray-400" />
                            </div>
                            <div className="space-y-4">
                                {stats.recentSurveys.filter(survey => survey.date === '2024-01-15').map((survey) => (
                                    <div key={survey.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                                <FontAwesomeIcon icon={faClipboardCheck} className="text-red-600" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900">{survey.title}</div>
                                                <div className="text-sm text-gray-600">Durée: {survey.duration}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <div className="flex items-center space-x-2">
                                                <FontAwesomeIcon 
                                                    icon={getStatusIcon(survey.status)} 
                                                    className={`text-sm ${getStatusColor(survey.status)}`}
                                                />
                                                <span className="text-sm text-gray-600">{getStatusText(survey.status)}</span>
                                            </div>
                                            <button className="px-3 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors">
                                                {survey.status === 'completed' ? 'Voir' : 'Continuer'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {stats.recentSurveys.filter(survey => survey.date === '2024-01-15').length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        Aucune enquête prévue pour aujourd'hui
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Performance Metrics */}
                        <div className="lg:col-span-4 bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-6 shadow-xl border border-green-200">
                            <div className="flex items-center mb-4">
                                <FontAwesomeIcon icon={faChartBar} className="text-2xl text-green-600 mr-3" />
                                <h3 className="text-xl font-bold text-gray-900">Performance</h3>
                            </div>
                            <div className="space-y-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-green-600">{stats.weeklyProgress}%</div>
                                    <div className="text-sm text-gray-600">Objectif Hebdomadaire</div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Enquêtes terminées</span>
                                        <span className="font-bold">{stats.completedSurveys}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Taux de réussite</span>
                                        <span className="font-bold text-green-600">92%</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Temps moyen</span>
                                        <span className="font-bold">42 min</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Performance Score Section */}
                        {interviewer && interviewer.accuracy_rating !== null && interviewer.accuracy_rating !== undefined && (
                            <div className="lg:col-span-12 bg-white border border-gray-200 rounded-3xl p-6 shadow-xl mb-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">Score de Performance</h3>
                                    {interviewer.score_updated_at && (
                                        <span className="text-xs text-gray-500">
                                            Mis à jour: {new Date(interviewer.score_updated_at).toLocaleDateString('fr-FR')}
                                        </span>
                                    )}
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Overall Score - Circular Gauge */}
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                                        <div className="text-sm text-gray-600 mb-4">Score Global</div>
                                        <div className="relative w-40 h-40">
                                            {/* SVG Circular Gauge */}
                                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                                                {/* Background circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#e5e7eb"
                                                    strokeWidth="12"
                                                />
                                                {/* Progress circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke={
                                                        interviewer.accuracy_rating >= 80 ? '#16a34a' :
                                                        interviewer.accuracy_rating >= 60 ? '#ca8a04' :
                                                        '#dc2626'
                                                    }
                                                    strokeWidth="12"
                                                    strokeLinecap="round"
                                                    strokeDasharray={`${2 * Math.PI * 54}`}
                                                    strokeDashoffset={`${2 * Math.PI * 54 * (1 - interviewer.accuracy_rating / 100)}`}
                                                    style={{
                                                        transition: 'stroke-dashoffset 1.5s ease-in-out',
                                                    }}
                                                />
                                            </svg>
                                            {/* Center text */}
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="text-4xl font-bold text-gray-900">
                                                    {Math.round(interviewer.accuracy_rating)}
                                                </span>
                                                <span className="text-sm text-gray-500">/ 100</span>
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-4">Performance globale</div>
                                    </div>
                                    
                                    {/* Productivity Component */}
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                                        <div className="text-sm text-gray-600 mb-4">Productivité</div>
                                        <div className="relative w-40 h-40">
                                            {/* SVG Circular Gauge */}
                                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                                                {/* Background circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#e5e7eb"
                                                    strokeWidth="12"
                                                />
                                                {/* Progress circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#2563eb"
                                                    strokeWidth="12"
                                                    strokeLinecap="round"
                                                    strokeDasharray={`${2 * Math.PI * 54}`}
                                                    strokeDashoffset={`${2 * Math.PI * 54 * (1 - (interviewer.prod_component || 0) / 100)}`}
                                                    style={{
                                                        transition: 'stroke-dashoffset 1.5s ease-in-out 0.3s',
                                                    }}
                                                />
                                            </svg>
                                            {/* Center text */}
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="text-4xl font-bold text-blue-600">
                                                    {interviewer.prod_component !== null ? Math.round(interviewer.prod_component) : '-'}
                                                </span>
                                                <span className="text-sm text-gray-500">/ 100</span>
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-4">Basé sur les complétions</div>
                                    </div>
                                    
                                    {/* Quality Component */}
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center">
                                        <div className="text-sm text-gray-600 mb-4">Qualité</div>
                                        <div className="relative w-40 h-40">
                                            {/* SVG Circular Gauge */}
                                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
                                                {/* Background circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#e5e7eb"
                                                    strokeWidth="12"
                                                />
                                                {/* Progress circle */}
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="54"
                                                    fill="none"
                                                    stroke="#16a34a"
                                                    strokeWidth="12"
                                                    strokeLinecap="round"
                                                    strokeDasharray={`${2 * Math.PI * 54}`}
                                                    strokeDashoffset={`${2 * Math.PI * 54 * (1 - (interviewer.qual_component || 0) / 100)}`}
                                                    style={{
                                                        transition: 'stroke-dashoffset 1.5s ease-in-out 0.6s',
                                                    }}
                                                />
                                            </svg>
                                            {/* Center text */}
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="text-4xl font-bold text-green-600">
                                                    {interviewer.qual_component !== null ? Math.round(interviewer.qual_component) : '-'}
                                                </span>
                                                <span className="text-sm text-gray-500">/ 100</span>
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-4">Basé sur les pénalités</div>
                                    </div>
                                </div>
                                
                                {/* Legend */}
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                    <p className="text-xs text-gray-600">
                                        <strong>Note:</strong> Le score est calculé automatiquement à partir de 60% Productivité + 40% Qualité, 
                                        basé sur les performances des 6 et 18 derniers mois (70% récent, 30% long terme).
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Recent Activity */}
                        <div className="lg:col-span-12 bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl p-6 shadow-xl border border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Activité Récente</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {stats.recentSurveys.slice(0, 6).map((survey) => (
                                    <div key={survey.id} className="bg-white p-4 rounded-xl border border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="font-medium text-gray-900 text-sm">{survey.title}</h4>
                                            <FontAwesomeIcon 
                                                icon={getStatusIcon(survey.status)} 
                                                className={`text-sm ${getStatusColor(survey.status)}`}
                                            />
                                        </div>
                                        <div className="text-xs text-gray-600 mb-2">{survey.date}</div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs text-gray-500">Durée: {survey.duration}</span>
                                            <span className={`text-xs px-2 py-1 rounded-full ${
                                                survey.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                survey.status === 'in_progress' ? 'bg-red-100 text-red-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {getStatusText(survey.status)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InterviewerDashboard;
